/**
 * Centralized fullscreen component utilities
 * Provides reusable FullscreenIcon component and fullscreen CSS classes
 * Used by: _4_today_trade_table.js, _12_calendar_year.js, and other components
 */

import React from "react";
import { getActiveModule } from "./useModuleVisibility.js";

/**
 * FullscreenIcon Button Component
 * Opens a component in fullscreen mode using module_name parameter
 * 
 * @param {string} moduleName - The MODULE_NAME identifier (e.g., "_4_today_trade_table")
 * @param {string} title - Button tooltip title
 * @param {object} additionalParams - Extra URL parameters to preserve
 * @returns {React.ReactElement}
 * 
 * @example
 * <FullscreenIcon 
 *   moduleName={MODULE_NAME}
 *   title="Equities Traded Table"
 *   additionalParams={{ date: effectiveDate }}
 * />
 */
export const FullscreenIcon = ({ moduleName, title = "Fullscreen View", additionalParams = {} }) => {
    const handleOpenFullscreen = () => {
        const baseUrl = window.location.origin + window.location.pathname;
        const params = new URLSearchParams({
            module_name: moduleName,
            fullscreen: "true",
            ...additionalParams
        });

        const fullUrl = `${baseUrl}?${params.toString()}`;
        const newWindow = window.open(
            fullUrl,
            `fullscreen-${moduleName}`,
            "width=1400,height=900,resizable=yes,scrollbars=yes"
        );

        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            alert('Popup blocked. Please enable popups for this site.');
        } else {
            newWindow.focus();
        }
    };

    return (
        <button
            onClick={handleOpenFullscreen}
            title={`Open ${title} in fullscreen window`}
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                padding: 0,
                backgroundColor: "#1e293b",
                border: "1px solid #475569",
                borderRadius: 4,
                cursor: "pointer",
                color: "#94a3b8",
                fontSize: 14,
                fontWeight: 600,
                transition: "all 0.2s ease",
                marginLeft: 8
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#334155";
                e.currentTarget.style.borderColor = "#64748b";
                e.currentTarget.style.color = "#cbd5e1";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#1e293b";
                e.currentTarget.style.borderColor = "#475569";
                e.currentTarget.style.color = "#94a3b8";
            }}
            data-analytics={moduleName}
        >
            ⛶
        </button>
    );
};

/**
 * Get fullscreen-aware container styles
 * Removes border, padding, and background in fullscreen mode
 * 
 * @param {boolean} isFullscreen - Whether component is in fullscreen mode
 * @param {number} defaultPadding - Padding when not in fullscreen (default: 20)
 * @returns {object} Style object for container
 * 
 * @example
 * <div style={getFullscreenContainerStyles(isFullscreen)}>
 */
export const getFullscreenContainerStyles = (isFullscreen, defaultPadding = 20) => ({
    background: isFullscreen ? "transparent" : "#181A25",
    borderRadius: isFullscreen ? 0 : 8,
    padding: isFullscreen ? "12px" : defaultPadding,
    margin: isFullscreen ? 0 : "0px 0",
    border: isFullscreen ? "none" : "2px solid #457b9d"
});

/**
 * Get fullscreen-aware header styles
 * Adjusts padding for fullscreen mode
 * 
 * @param {boolean} isFullscreen - Whether component is in fullscreen mode
 * @returns {object} Style object for header container
 */
export const getFullscreenHeaderStyles = (isFullscreen) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: isFullscreen ? "0 12px" : "0"
});

/**
 * Get fullscreen-aware content styles
 * Adjusts padding for fullscreen mode
 * 
 * @param {boolean} isFullscreen - Whether component is in fullscreen mode
 * @returns {object} Style object for content container
 */
export const getFullscreenContentStyles = (isFullscreen) => ({
    padding: isFullscreen ? "12px 12px 12px 12px" : "0"
});

/**
 * Get fullscreen-aware layout styles
 * Used for flex containers that need to expand in fullscreen
 * 
 * @param {boolean} isFullscreen - Whether component is in fullscreen mode
 * @param {number} defaultGap - Gap between items when not fullscreen (default: 40)
 * @param {number} fullscreenGap - Gap between items in fullscreen (default: 60)
 * @returns {object} Style object for layout container
 */
export const getFullscreenLayoutStyles = (isFullscreen, defaultGap = 40, fullscreenGap = 60) => ({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: isFullscreen ? fullscreenGap : defaultGap,
    marginTop: 16,
    marginBottom: 0,
    overflowX: "visible",
    paddingBottom: isFullscreen ? 16 : 0,
    width: "100%"
});

/**
 * Hook to set fullscreen data attribute on HTML element
 * Allows CSS to target fullscreen state globally
 * 
 * @param {boolean} isFullscreen - Whether component is in fullscreen mode
 * 
 * @example
 * useFullscreenAttribute(isFullscreen);
 */
export const useFullscreenAttribute = (isFullscreen) => {
    React.useEffect(() => {
        if (isFullscreen) {
            document.documentElement.setAttribute('data-fullscreen', 'true');
            return () => document.documentElement.removeAttribute('data-fullscreen');
        }
    }, [isFullscreen]);
};